import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Linkedin, Mail, MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import { team } from "../mock";
import useSeo from "../hooks/useSeo";

const TeamMember = () => {
  const { slug } = useParams();
  const m = team.find((t) => t.slug === slug);
  useSeo({
    title: m ? `${m.role} — ${m.department}` : "Team",
    description: m?.long || m?.bio,
    image: m?.image,
    type: "profile",
    path: `/team/${slug}`,
  });
  if (!m) return <Navigate to="/about" replace />;

  return (
    <>
      <section className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <Link to="/about" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to team
          </Link>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="relative">
              <div className="absolute -inset-3 bg-teal-500/20 rounded-3xl blur-2xl" />
              <img src={m.image} alt={m.role} className="relative rounded-2xl w-full aspect-[4/5] object-cover shadow-2xl" />
            </div>
            <div className="md:col-span-2">
              <p className="text-teal-400 font-bold tracking-widest text-xs">{m.department.toUpperCase()}</p>
              <h1 className="mt-2 text-4xl md:text-5xl font-extrabold">{m.role}</h1>
              <p className="mt-6 text-neutral-200 leading-relaxed">{m.long}</p>
              <div className="mt-6 flex flex-wrap gap-4 text-sm text-neutral-300">
                <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-teal-400" /> {m.location}</span>
                <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 hover:text-teal-400 transition-colors"><Mail className="w-4 h-4 text-teal-400" /> {m.email}</a>
                <a href={m.linkedin} className="inline-flex items-center gap-2 hover:text-teal-400 transition-colors"><Linkedin className="w-4 h-4 text-teal-400" /> LinkedIn</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Areas of expertise</h2>
            <ul className="mt-4 space-y-3">
              {m.expertise.map((e) => (
                <li key={e} className="flex items-start gap-3 text-neutral-700">
                  <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Recent engagements</h2>
            <ul className="mt-4 space-y-4">
              {m.engagements.map((e) => (
                <li key={e.title} className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                  <div className="text-xs font-bold text-teal-600 tracking-widest">{e.year}</div>
                  <div className="mt-1 text-neutral-900 font-semibold">{e.title}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Want to work with our {m.role}?</h3>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3 rounded-full transition-colors">
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default TeamMember;
